<script>
    // Step 3 of onboarding, and the "what now" screen after any rating:
    // a full routine, just the products you need, or a check on one product.
    //
    // Specific products are checked against the routine the person already
    // uses. The engine moves picks that clash with it (an acid next to a
    // retinoid, say) behind the ones that do not, and labels them.
    import { onMount } from 'svelte';
    import { navigate } from '../router.js';
    import Button from '../components/Button.svelte';
    import Card from '../components/Card.svelte';
    import { requestRecommendations, storedRoutine } from '../recommend.js';

    const TYPES = [
        { value: 'cleanser', label: 'Cleanser' },
        { value: 'toner', label: 'Toner' },
        { value: 'serum', label: 'Serum' },
        { value: 'treatment', label: 'Treatment' },
        { value: 'exfoliant', label: 'Exfoliant' },
        { value: 'eye_cream', label: 'Eye cream' },
        { value: 'moisturizer', label: 'Moisturizer' },
        { value: 'night_cream', label: 'Night cream' },
        { value: 'sunscreen', label: 'Sunscreen' },
        { value: 'face_mask', label: 'Mask' }
    ];

    // A fix from the routine rating that names a missing step pre-selects it.
    const FIX_TO_TYPE = [
        [/daily spf|spf to the morning/i, 'sunscreen'],
        [/moisturizer/i, 'moisturizer'],
        [/cleanser/i, 'cleanser'],
        [/add something for/i, 'serum']
    ];

    let rating = null;
    let routineCount = 0;
    let chosen = [];
    let loading = '';
    let error = '';

    onMount(() => {
        try { rating = JSON.parse(localStorage.getItem('routineRating') || 'null'); } catch (e) { rating = null; }
        routineCount = storedRoutine().length;
        if (rating && rating.fixes) {
            for (const title of rating.fixes) {
                for (const [pattern, type] of FIX_TO_TYPE) {
                    if (pattern.test(title) && !chosen.includes(type)) chosen = [...chosen, type];
                }
            }
        }
    });

    function toggle(type) {
        chosen = chosen.includes(type) ? chosen.filter(t => t !== type) : [...chosen, type];
    }

    async function go(kind) {
        loading = kind;
        error = '';
        const result = await requestRecommendations(kind === 'specific' ? { productTypes: chosen } : {});
        if (!result.ok) error = result.error || 'Something went wrong. Please try again.';
        loading = '';
    }
</script>

<div class="u-page next">
    <header>
        <p class="eyebrow">{rating ? 'Step 4 of 4 · What’s next' : 'What’s next'}</p>
        <h1>What would you like SkinGenie to find?</h1>
        {#if rating}
            <p class="lead">Your routine scored <strong>{rating.score}</strong>. {rating.verdict}</p>
        {:else}
            <p class="lead">Recommendations are built around your skin analysis{routineCount ? ' and the routine you already use' : ''}.</p>
        {/if}
    </header>

    <div class="choices">
        <Card>
            <h2>A full routine</h2>
            <p class="muted">Morning and night, built around your skin{routineCount ? '. Keep what works and swap what doesn’t' : ''}.</p>
            <Button loading={loading === 'full'} busyLabel="Building your routine" on:click={() => go('full')}>Build my routine</Button>
        </Card>

        <Card>
            <h2>Just the products I need</h2>
            <p class="muted">Pick the steps you want. {routineCount ? 'Picks that clash with your routine come last, with a note.' : ''}</p>
            <div class="types" role="group" aria-label="Product types">
                {#each TYPES as t}
                    <button type="button" class="type" class:on={chosen.includes(t.value)} aria-pressed={chosen.includes(t.value)} on:click={() => toggle(t.value)}>{t.label}</button>
                {/each}
            </div>
            <Button variant="secondary" disabled={chosen.length === 0} loading={loading === 'specific'} busyLabel="Finding products" on:click={() => go('specific')}>
                {chosen.length ? `Find ${chosen.length} product type${chosen.length === 1 ? '' : 's'}` : 'Pick at least one'}
            </Button>
        </Card>

        <Card>
            <h2>Check one product</h2>
            <p class="muted">Scan, search or paste a product to see whether it suits your skin and your routine.</p>
            <Button variant="secondary" on:click={() => navigate('scanner')}>Check a product</Button>
        </Card>
    </div>

    {#if error}<p class="error" aria-live="polite">{error}</p>{/if}
</div>

<style>
    .next { font-family: var(--font-body); }
    header { max-width: var(--prose-max); margin-bottom: var(--space-10); }
    .eyebrow {
        color: var(--sage);
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-eyebrow);
        text-transform: uppercase;
    }
    h1 {
        font-family: var(--font-display);
        font-weight: var(--weight-regular);
        font-size: var(--text-4xl);
        line-height: var(--leading-tight);
        margin: var(--space-3) 0 var(--space-4);
    }
    .lead { color: var(--text-muted); line-height: var(--leading-relaxed); }
    .lead strong { color: var(--text); }
    .choices {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: var(--space-4);
        align-items: start;
    }
    h2 {
        font-family: var(--font-display);
        font-weight: var(--weight-regular);
        font-size: var(--text-xl);
        margin: 0 0 var(--space-2);
    }
    .muted {
        color: var(--text-muted);
        line-height: var(--leading-relaxed);
        margin: 0 0 var(--space-5);
    }
    .types {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
        margin-bottom: var(--space-5);
    }
    .type {
        font: inherit;
        font-size: var(--text-sm);
        padding: var(--space-2) var(--space-4);
        border-radius: var(--radius-pill);
        border: 1px solid var(--border-strong);
        background: transparent;
        color: var(--text);
        margin: 0;
        cursor: pointer;
        transition: var(--transition);
    }
    .type:hover { background: var(--hover-veil); }
    .type.on {
        background: var(--sage-veil);
        border-color: var(--sage);
        color: var(--sage-strong);
    }
    .error { color: var(--danger); margin-top: var(--space-6); }
</style>
